import { CircleAlertIcon } from "lucide-react";

import { resolveCodexCreditGate, type ComposerCreditGateDecision } from "./composerCreditGate";
import { useProviderUsageRead } from "~/usage/useProviderUsage";

/**
 * FOR-32770: pre-send headroom check for Codex accounts. Warn on windows full
 * plus remaining credits (sending draws the balance down); block only on
 * windows full, zero credits, and a fresh reading. Every unknown degrades to
 * at most a warning, because a false block stops real work.
 */
export function useComposerSendGate(input: {
  readonly provider: string;
  readonly providerAccountId: string | undefined;
  readonly promptLengthError: string | null;
  readonly onSend: (event?: { preventDefault: () => void }) => void;
  readonly onPromptLengthError: () => void;
}): {
  readonly decision: ComposerCreditGateDecision;
  readonly guardSubmit: (event?: { preventDefault: () => void }) => void;
} {
  const accounts = useProviderUsageRead().accounts;
  const decision = resolveCodexCreditGate({
    provider: input.provider,
    providerAccountId: input.providerAccountId,
    accounts,
    nowMs: Date.now(),
  });
  const guardSubmit = (event?: { preventDefault: () => void }) => {
    if (input.promptLengthError !== null) {
      event?.preventDefault();
      input.onPromptLengthError();
      return;
    }
    if (decision.kind === "block") {
      event?.preventDefault();
      return;
    }
    input.onSend(event);
  };
  return { decision, guardSubmit };
}

/**
 * A refused send needs its reason at the point of refusal: the notice renders
 * directly above the footer so the message and the send button read together.
 */
export function ComposerCreditGateNotice({
  decision,
}: {
  readonly decision: ComposerCreditGateDecision;
}) {
  if (decision.kind === "ok") return null;
  return (
    <p
      role="status"
      data-credit-gate={decision.kind}
      className={`flex items-center gap-1.5 px-3 pb-1 text-xs sm:px-4 ${
        decision.kind === "block" ? "text-destructive-foreground" : "text-warning-foreground"
      }`}
    >
      <CircleAlertIcon aria-hidden="true" className="size-3.5 shrink-0" />
      {decision.message}
    </p>
  );
}
